import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { catchError, map, of, switchMap } from "rxjs";
import { OmniarkUiModule, SelectOption } from "@corp-ais/omniark-ui";
import { MastersService } from "@app/core/services/masters.service";
import { LanguageModeService } from "@app/shared";
import {
  normalizeSegmentMaster,
  PACKAGE_TYPE_VALUES,
  PRODUCT_EFFECTIVE_OPTION_META,
  SEGMENT_FALLBACK,
} from "../../enrichment-tab/enrichment-tab-product-attributes/enrichment-tab-product-attributes";

/** The four Price Classification fields (CONTEXT.md). */
export type PriceClassificationField =
  "networkType" | "segment" | "packageType" | "productEffectiveOption";

/** A price's (or the offering's) classification values. `null`/absent = no
 *  value of its own; `[]` = explicitly cleared. */
export type PriceClassificationValues = Partial<
  Record<PriceClassificationField, string[] | null>
>;

export interface PriceClassificationChange {
  field: PriceClassificationField;
  values: string[];
}

/**
 * Pricing tab → "Classification" section: the price's own Network Type /
 * Package Type / Segment / Product effective option.
 *
 * Presentational: the parent owns the price and writes each change back
 * (`fieldChange`). A field the price has no value for (`null`/absent) shows
 * the offering's value as a hint; an explicitly cleared field (`[]`) does
 * not — the price really has no value downstream.
 */
@Component({
  selector: "app-enrichment-price-classification",
  standalone: true,
  imports: [CommonModule, OmniarkUiModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./enrichment-price-classification.html",
  styleUrls: ["./enrichment-price-classification.scss"],
})
export class EnrichmentPriceClassification {
  private readonly masters = inject(MastersService);
  private readonly languageMode = inject(LanguageModeService);

  readonly price = input<PriceClassificationValues | null>(null);
  /** The offering-level values (Offering Classification) used for the hint. */
  readonly offering = input<PriceClassificationValues | null>(null);
  /** myAIS edit scope — gates networkType / packageType / productEffectiveOption. */
  readonly isMyAis = input<boolean>(false);
  /** Network Type edit scope (myAIS + AI Chatbot); required only on myAIS. */
  readonly showNetworkType = input<boolean>(false);
  /** Flat `__detail.productType` — filters the nType master and drops the
   *  "Next billing cycle" effective option for prepaid. */
  readonly productType = input<string | undefined>(undefined);

  readonly fieldChange = output<PriceClassificationChange>();

  private readonly isPrepaid = computed(
    () =>
      (this.productType() ?? "").toLowerCase().replaceAll(/[^a-z]/g, "") ===
      "prepaid",
  );

  /** nType master, reloaded when productType changes (same mapping as the
   *  Product-attributes tab). */
  private readonly networkTypeMaster = toSignal(
    toObservable(this.productType).pipe(
      switchMap((productType) =>
        this.masters.getMasterNtype(productType).pipe(
          map((res) =>
            (res?.data ?? [])
              .filter((r) => r?.useFlag === "Y" && !!r?.ntypeName)
              .map((r) => ({
                value: r.ntypeName,
                label: r.mobileType
                  ? `${r.mobileType} (${r.ntypeName})`
                  : r.ntypeName,
              })),
          ),
          catchError(() => of([] as SelectOption[])),
        ),
      ),
    ),
    { initialValue: [] as SelectOption[] },
  );

  private readonly segmentMaster = toSignal(
    this.masters.getMasterSegment().pipe(
      map((res) => {
        const rows = (Array.isArray(res?.data) ? res.data : [])
          .map((r) => normalizeSegmentMaster(r))
          .filter((o): o is SelectOption => !!o);
        return rows.length ? rows : segmentFallback();
      }),
      catchError(() => of(segmentFallback())),
    ),
    { initialValue: segmentFallback() },
  );

  /** Effective-option master for the view language (1 = TH, 2 = EN). */
  private readonly effectiveMaster = toSignal(
    toObservable(
      computed(() =>
        this.languageMode.activeLocale().toLowerCase().startsWith("th") ? 1 : 2,
      ),
    ).pipe(
      switchMap((languageId) =>
        this.masters.getMasterEffective(languageId).pipe(
          map((res) =>
            (res?.data ?? [])
              .filter((r) => (r.useFlag ?? "Y") === "Y" && !!r.option)
              .map((r) => ({ value: r.option, label: r.topic })),
          ),
          catchError(() => of([] as SelectOption[])),
        ),
      ),
    ),
    { initialValue: [] as SelectOption[] },
  );

  readonly networkTypeOptions = computed(() =>
    withStored(this.networkTypeMaster(), this.storedValues("networkType")),
  );
  readonly segmentOptions = computed(() =>
    withStored(this.segmentMaster(), this.storedValues("segment")),
  );
  readonly packageTypeOptions: SelectOption[] = PACKAGE_TYPE_VALUES.map(
    (v) => ({ value: v, label: v }),
  );
  readonly effectiveOptions = computed(() => {
    const fromApi = this.effectiveMaster();
    const base = fromApi.length
      ? fromApi
      : PRODUCT_EFFECTIVE_OPTION_META.map((m) => ({
          value: m.option,
          label: m.topic,
        }));
    return base.filter((o) => !(this.isPrepaid() && o.value === "B"));
  });

  readonly networkType = computed(() => this.price()?.networkType ?? []);
  readonly segment = computed(() => this.price()?.segment ?? []);
  readonly packageType = computed(() => this.price()?.packageType?.[0] ?? "");
  readonly productEffectiveOption = computed(
    () => this.price()?.productEffectiveOption ?? [],
  );

  readonly networkTypeHint = computed(() =>
    this.hintFor("networkType", this.networkTypeOptions()),
  );
  readonly segmentHint = computed(() =>
    this.hintFor("segment", this.segmentOptions()),
  );
  readonly packageTypeHint = computed(() =>
    this.hintFor("packageType", this.packageTypeOptions),
  );
  readonly effectiveHint = computed(() =>
    this.hintFor("productEffectiveOption", this.effectiveOptions()),
  );

  onMultiChange(field: PriceClassificationField, value: unknown): void {
    this.fieldChange.emit({ field, values: toStringArray(value) });
  }

  onPackageTypeChange(value: unknown): void {
    const v = typeof value === "string" ? value : "";
    this.fieldChange.emit({ field: "packageType", values: v ? [v] : [] });
  }

  /** "Offering: X, Y" when the price has no value of its own and the
   *  offering has one; empty otherwise (incl. a price cleared to `[]`). */
  private hintFor(
    field: PriceClassificationField,
    options: readonly SelectOption[],
  ): string {
    const own = this.price()?.[field];
    if (own != null) return "";
    const inherited = this.offering()?.[field] ?? [];
    if (!inherited.length) return "";
    const labels = inherited.map(
      (v) => options.find((o) => o.value === v)?.label ?? v,
    );
    return `Offering value: ${labels.join(", ")}`;
  }

  private storedValues(field: PriceClassificationField): string[] {
    return [
      ...(this.price()?.[field] ?? []),
      ...(this.offering()?.[field] ?? []),
    ];
  }
}

function segmentFallback(): SelectOption[] {
  return SEGMENT_FALLBACK.map((s) => ({ value: s.id, label: s.label }));
}

/** Keep a stored value the master no longer lists selectable/visible. */
function withStored(
  options: SelectOption[],
  stored: readonly string[],
): SelectOption[] {
  const extra = [...new Set(stored)].filter(
    (v) => v && !options.some((o) => o.value === v),
  );
  return extra.length
    ? [...options, ...extra.map((v) => ({ value: v, label: v }))]
    : options;
}

function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === "string" && !!v);
  }
  return typeof value === "string" && value ? [value] : [];
}
